import { computeDistanceBetween } from "@bmi-digital/components/google-api";
import uniqBy from "lodash-es/uniqBy";
import {
  DEFAULT_MAP_CENTRE,
  EVENT_CAT_ID_SELECTOR_CARDS,
  EVENT_CAT_ID_SELECTOR_CARDS_MAP_PIN,
  FILTER_RADIUS
} from "./constants";
import type {
  LatLngLiteral,
  MarkerOptionsWithData
} from "@bmi-digital/components/google-api";
import type { Service, ServiceData, ServiceTypeFilter } from "../Service";
import type { ServiceTypeData } from "../ServiceType";

export const getTypesFromServices = (
  services: Service[]
): ServiceTypeData[] => {
  return uniqBy(
    services.reduce<ServiceTypeData[]>((acc, service) => {
      if (service.serviceTypes) {
        return [...acc, ...service.serviceTypes];
      }
      return acc;
    }, []),
    "name"
  );
};

export const getRooferTypes = (
  uniqueRoofTypeByData: ServiceTypeData[],
  allQueries: string[]
): ServiceTypeData[] => {
  return uniqueRoofTypeByData.reduce<ServiceTypeData[]>((types, type) => {
    allQueries.find(
      (query) => query.toLowerCase() === type.name.toLowerCase()
    ) && types.push(type);
    return types;
  }, []);
};

export const createMarker = (selectedRoofer: Service | null) => {
  return (service: Service): MarkerOptionsWithData<Service> => ({
    title: `${service.name}-${service.id}`,
    position: {
      lng: service.location[0],
      lat: service.location[1]
    },
    isActive: selectedRoofer?.id === service.id,
    data: service,
    "data-gtm": JSON.stringify(getResultDataGtm(service, true))
  });
};

export const getDistanceSort = (
  centre: LatLngLiteral | undefined,
  serviceA: Service,
  serviceB: Service
): number => {
  const distanceSort = centre
    ? (serviceA.distance ?? 0) - (serviceB.distance ?? 0)
    : 0;

  return distanceSort;
};

export const sortServices = (centre: LatLngLiteral | undefined) => {
  return (serviceA: Service, serviceB: Service): number => {
    if (getDistanceSort(centre, serviceA, serviceB) === 0) {
      const serviceNameA = serviceA.name.toLowerCase();
      const serviceNameB = serviceB.name.toLowerCase();

      if (serviceNameA === serviceNameB) {
        return 0;
      } else {
        // according to the issue (https://bmigroup.atlassian.net/browse/DXB-3542) with special characters Ç Ş changed rule for sorting to localeCompare
        return serviceNameA.localeCompare(serviceNameB);
      }
    }

    return getDistanceSort(centre, serviceA, serviceB);
  };
};

export const typeFilter = (
  typeData: ServiceTypeData[],
  activeFilters: ServiceTypeFilter
): boolean => {
  // const { type, branchType, merchantType } = typeData;
  // user has not selected any chip(s) to filter hence show all services
  // i.e initial load or user has de-selected ALL chips
  const allChipsUnSelected = Object.keys(activeFilters).every(
    // eslint-disable-next-line security/detect-object-injection
    (key) => activeFilters[key] === false
  );

  if (allChipsUnSelected) {
    return true;
  }

  // user has selected the filter chip the service must have type and it must match
  const isSomeChipSelected = Object.keys(activeFilters).some(
    // eslint-disable-next-line security/detect-object-injection
    (key) => activeFilters[key] === true
  );

  if (isSomeChipSelected) {
    // eslint-disable-next-line security/detect-object-injection
    return typeData?.some((filter) => activeFilters[filter.name]) || false;
  }
  // service's types are null hence return true
  return true;
};

export const filterServices = (
  centre: LatLngLiteral | undefined,
  activeFilters: ServiceTypeFilter,
  activeSearchString: string
): ((carry: ServiceData[], current: ServiceData) => Service[]) => {
  return (carry, current) => {
    const { name, location, serviceTypes } = current;
    const isServiceNameIncludingSearchStr = name
      .toLowerCase()
      .includes(activeSearchString.toLowerCase());
    const distance = centre
      ? computeDistanceBetween(centre, {
          lng: location[0],
          lat: location[1]
        })
      : undefined;
    const isServiceInRadius = distance ? distance < FILTER_RADIUS : true;
    const isServiceTypeChosen = typeFilter(serviceTypes, activeFilters);
    if (
      isServiceTypeChosen &&
      isServiceNameIncludingSearchStr &&
      isServiceInRadius
    ) {
      return [
        ...carry,
        {
          ...current,
          distance
        }
      ];
    }

    return carry;
  };
};

export const getResultDataGtm = (
  service: Service,
  isMarker = false
): { id: string; label: string; action: string; event: string } => {
  const { name, address, certification, serviceTypes, entryType } = service;
  const label = `${name} - ${address}${
    certification ? ` - ${certification}` : ""
  }${
    serviceTypes && serviceTypes.length === 1
      ? ` - ${serviceTypes[0].name}`
      : ` - ${entryType}`
  } - selected`;
  return {
    id: isMarker
      ? EVENT_CAT_ID_SELECTOR_CARDS_MAP_PIN
      : EVENT_CAT_ID_SELECTOR_CARDS,
    event: "dxb.button_click",
    label: label,
    action: "Expanded company details"
  };
};

export const getFilterOptions = (
  options: string[],
  { inputValue }: { inputValue: string }
): string[] => {
  if (inputValue.length < 2) {
    return [];
  }

  const lowerCaseInputValue = inputValue.toLowerCase();
  const seen = new Set<string>();

  return options.filter((option) => {
    const lowerCaseOption = option.toLowerCase();

    if (
      lowerCaseOption.includes(lowerCaseInputValue) &&
      !seen.has(lowerCaseOption)
    ) {
      seen.add(lowerCaseOption);
      return true;
    }
    return false;
  });
};

export const calculateCentre = (
  centre: LatLngLiteral | null,
  initialMapCentre: [number, number] | null
): google.maps.LatLngLiteral => {
  return (
    centre ||
    (initialMapCentre && {
      lng: initialMapCentre[0],
      lat: initialMapCentre[1]
    }) ||
    DEFAULT_MAP_CENTRE
  );
};
